import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { auditProjectDeletion, auditOwnershipTransfer, logAuditEvent, AuditAction } from './audit';
import { getProjectState, BUCKET_TO_STATUS_MAP, type CoreBucket } from './project-states';
import { User } from 'firebase/auth';
import { canCreateProject } from './subscriptions';
import { logProjectActivity } from './project-activity';
import { SubscriptionLimitError } from './errors';

export interface ProjectSource {
  type: 'slack' | 'jira' | 'linear' | 'meeting' | 'other';
  url?: string;
  summary?: string;
  draftedBy: 'claude' | 'user';
}

export interface Project {
  id?: string;
  name: string;
  description: string;
  timeline: {
    start: string;
    end: string;
  };
  figmaUrl?: string;
  showFigmaEmbed?: boolean; // Whether to show Figma embed on project page (default: true)
  resources?: { url: string; label: string }[]; // Project resources (links to docs, designs, etc.)
  goal?: string;
  successDefinition?: string;
  contributors: string[];
  teamId?: string; // Legacy field for single team (kept for backwards compatibility)
  teamIds?: string[]; // New field for multiple team associations
  status: 'planning' | 'active' | 'review' | 'completed' | 'archived'; // Legacy field, kept for migration
  stateId?: string; // Reference to org project state
  stateCoreBucket?: 'planned' | 'active' | 'done'; // Denormalized for queries
  labelIds?: string[];
  createdBy: string;
  ownerUserId?: string; // Project owner (can be different from creator)
  visibility?: 'org' | 'team' | 'private'; // Project visibility scope
  // Hard identifiers from systems-of-record (Jira issue key, Linear ID, etc.).
  // Distinct from `source`, which describes how a draft was assembled.
  externalId?: string; // External ticket ID (e.g. "ENG-123", "DC-45")
  externalSource?: 'linear' | 'jira' | 'github'; // Source system
  externalUrl?: string; // Direct link to the external ticket
  externalSyncEnabled?: boolean; // Whether to sync updates to the external ticket
  // Draft state — set when an MCP caller (Claude) creates a project that needs
  // human review before it appears in normal listings. Accepting a draft flips
  // this to false.
  isDraft?: boolean;
  source?: ProjectSource;
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
  updatedAt?: Timestamp | ReturnType<typeof serverTimestamp>;
  completedAt?: Timestamp | ReturnType<typeof serverTimestamp>;
  deletedAt?: Timestamp | null; // Soft delete support
  isExample?: boolean; // Example content flag
}

export async function createProject(
  orgId: string,
  project: Omit<Project, 'id' | 'createdAt'>,
  actorName?: string
) {
  // Check subscription limits
  const limitCheck = await canCreateProject(orgId);
  if (!limitCheck.allowed) {
    throw new SubscriptionLimitError(
      limitCheck.reason || 'Cannot create project: limit reached',
      'projects',
      limitCheck.currentCount,
      limitCheck.limit
    );
  }

  const projectRef = doc(collection(db, 'orgs', orgId, 'projects'));

  // Ensure required fields have default values
  const projectData: Record<string, unknown> = {
    name: project.name || '',
    description: project.description || '',
    goal: project.goal || '',
    timeline: project.timeline || { start: '', end: '' },
    contributors: Array.isArray(project.contributors) ? project.contributors : [],
    status: project.status || 'planning',
    createdBy: project.createdBy,
    createdAt: serverTimestamp(),
    deletedAt: null,
    // Set owner to creator by default
    ownerUserId: project.ownerUserId || project.createdBy,
    // Default visibility to team if teamId/teamIds is provided, otherwise org
    visibility: project.visibility || (project.teamId || (project.teamIds && project.teamIds.length > 0) ? 'team' : 'org')
  };

  // Add optional fields only if they have values
  if (project.figmaUrl) projectData.figmaUrl = project.figmaUrl;
  if (project.successDefinition) projectData.successDefinition = project.successDefinition;

  // Handle both single teamId and multiple teamIds
  if (project.teamIds && project.teamIds.length > 0) {
    projectData.teamIds = project.teamIds;
    // Also set legacy teamId to first team for backwards compatibility
    projectData.teamId = project.teamIds[0];
  } else if (project.teamId) {
    // If only teamId is provided, set both teamId and teamIds
    projectData.teamId = project.teamId;
    projectData.teamIds = [project.teamId];
  }

  if (project.stateId) projectData.stateId = project.stateId;
  if (project.stateCoreBucket) projectData.stateCoreBucket = project.stateCoreBucket;
  if (project.labelIds && project.labelIds.length > 0) projectData.labelIds = project.labelIds;

  try {
    await setDoc(projectRef, projectData);

    // Track in PostHog
    try {
      const { trackAppEvents } = await import('./posthog');
      trackAppEvents.createProject(project.name);
    } catch {}

    // Log project activity
    await logProjectActivity(orgId, {
      projectId: projectRef.id,
      type: 'project_created',
      actorId: project.createdBy,
      actorName
    });

    // Also log to team activity if project belongs to teams
    const teamIdsToLog = project.teamIds || (project.teamId ? [project.teamId] : []);
    if (teamIdsToLog.length > 0) {
      const { logTeamActivity } = await import('./team-activity');
      for (const teamId of teamIdsToLog) {
        await logTeamActivity(orgId, {
          teamId,
          type: 'project_created',
          actorId: project.createdBy,
          actorName,
          metadata: {
            projectId: projectRef.id,
            projectName: project.name
          }
        });
      }
    }

    return projectRef.id;
  } catch (error: any) {
    // If the error is about document already existing, it means this is a duplicate submission
    // Return the existing project ID instead of failing
    if (error?.code === 'already-exists' || error?.message?.includes('already exists')) {
      console.warn('Project creation attempted twice, returning existing project ID:', projectRef.id);
      return projectRef.id;
    }
    // Re-throw other errors
    throw error;
  }
}

export async function getOrgProjects(orgId: string, includeDeleted = false, includeDrafts = false) {
  const projectsQuery = query(
    collection(db, 'orgs', orgId, 'projects'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(projectsQuery);

  const projects = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Project[];

  // Filter out soft-deleted and drafts unless explicitly requested
  return projects.filter(p => {
    if (!includeDeleted && p.deletedAt) return false;
    if (!includeDrafts && p.isDraft) return false;
    return true;
  });
}

// Get projects visible to a specific user (filters out projects from private teams where user is not a member)
export async function getVisibleOrgProjects(orgId: string, userId: string, includeDeleted = false, includeDrafts = false) {
  const projectsQuery = query(
    collection(db, 'orgs', orgId, 'projects'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(projectsQuery);

  const projects = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Project[];

  // Filter out soft-deleted projects, and drafts (drafts live in the user's
  // Drafts rail, not in normal listings — fetch via getUserDrafts).
  const activeProjects = projects.filter(p => {
    if (!includeDeleted && p.deletedAt) return false;
    if (!includeDrafts && p.isDraft) return false;
    return true;
  });

  // Filter out projects from private teams where user is not a member
  const { getTeam, getTeamMembership } = await import('./teams');
  const visibleProjects = [];

  for (const project of activeProjects) {
    // Get all team IDs associated with this project
    const projectTeamIds = project.teamIds || (project.teamId ? [project.teamId] : []);

    // Private projects are only visible to the owner
    if (project.visibility === 'private') {
      if (project.ownerUserId === userId || project.createdBy === userId) {
        visibleProjects.push(project);
      }
      continue;
    }

    if (projectTeamIds.length > 0) {
      // Project belongs to one or more teams, check if user has access to any of them
      let hasAccess = false;

      for (const teamId of projectTeamIds) {
        const team = await getTeam(orgId, teamId);
        if (team && team.membershipType === 'private') {
          // Team is private, check if user is a member
          const membership = await getTeamMembership(orgId, teamId, userId);
          if (membership) {
            hasAccess = true;
            break; // User has access via at least one team
          }
        } else {
          // Team is open/closed or doesn't exist, project is visible
          hasAccess = true;
          break;
        }
      }

      if (hasAccess) {
        visibleProjects.push(project);
      }
    } else {
      // Project doesn't belong to any team, it's visible
      visibleProjects.push(project);
    }
  }

  return visibleProjects;
}

// Drafts the given user has created and not yet accepted. Used by the Drafts
// rail surface on /projects. Drafts are not visible to anyone else until
// accepted (see firestore.rules), so we only filter by createdBy.
export async function getUserDrafts(orgId: string, userId: string): Promise<Project[]> {
  const draftsQuery = query(
    collection(db, 'orgs', orgId, 'projects'),
    where('isDraft', '==', true),
    where('createdBy', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(draftsQuery);
  return snapshot.docs
    .map(d => ({ id: d.id, ...d.data() }) as Project)
    .filter(p => !p.deletedAt);
}

export async function acceptProjectDraft(orgId: string, projectId: string) {
  const projectRef = doc(db, 'orgs', orgId, 'projects', projectId);
  await updateDoc(projectRef, {
    isDraft: false,
    updatedAt: serverTimestamp(),
  });
}

export async function discardProjectDraft(orgId: string, projectId: string) {
  // Drafts have never been visible to anyone else, so hard-delete rather than
  // soft-delete — keeps the projects collection clean.
  const projectRef = doc(db, 'orgs', orgId, 'projects', projectId);
  await deleteDoc(projectRef);
}

export async function getProject(orgId: string, projectId: string) {
  const projectDoc = await getDoc(doc(db, 'orgs', orgId, 'projects', projectId));
  
  if (!projectDoc.exists()) {
    return null;
  }
  
  return {
    id: projectDoc.id,
    ...projectDoc.data()
  } as Project;
}

export async function updateProject(
  orgId: string,
  projectId: string,
  updates: Partial<Project>,
  actorId?: string,
  actorName?: string
) {
  const projectRef = doc(db, 'orgs', orgId, 'projects', projectId);

  // Get current project data for comparison
  const currentProject = await getDoc(projectRef);
  const currentData = currentProject.exists() ? currentProject.data() : null;

  // Clean up the updates object - remove undefined values but keep empty strings and empty arrays
  const cleanUpdates: Record<string, any> = {};
  Object.keys(updates).forEach(key => {
    const value = updates[key as keyof Project];
    if (value !== undefined) {
      cleanUpdates[key] = value;
    }
  });

  // Handle completedAt timestamp based on status changes
  if (cleanUpdates.status && currentData) {
    if (cleanUpdates.status === 'completed' && currentData.status !== 'completed') {
      // Status is being changed to completed, add completedAt timestamp
      cleanUpdates.completedAt = serverTimestamp();
    } else if (cleanUpdates.status !== 'completed' && currentData.status === 'completed') {
      // Status is being changed from completed to something else, remove completedAt
      cleanUpdates.completedAt = null;
    }
  }

  // Always add updatedAt timestamp
  cleanUpdates.updatedAt = serverTimestamp();

  await updateDoc(projectRef, cleanUpdates);

  // Log activity changes if we have actor info
  if (actorId && currentData) {
    // Log name change
    if (cleanUpdates.name && cleanUpdates.name !== currentData.name) {
      await logProjectActivity(orgId, {
        projectId,
        type: 'name_changed',
        actorId,
        actorName,
        metadata: {
          fromValue: currentData.name ?? '',
          toValue: cleanUpdates.name
        }
      });
    }

    // Log description change
    if (cleanUpdates.description !== undefined && cleanUpdates.description !== currentData.description) {
      await logProjectActivity(orgId, {
        projectId,
        type: 'description_changed',
        actorId,
        actorName
      });
    }

    // Log timeline change
    if (cleanUpdates.timeline) {
      const oldTimeline = currentData.timeline;
      const newTimeline = cleanUpdates.timeline;
      if (JSON.stringify(oldTimeline) !== JSON.stringify(newTimeline)) {
        await logProjectActivity(orgId, {
          projectId,
          type: 'timeline_changed',
          actorId,
          actorName,
          metadata: {
            fromValue: oldTimeline ?? { start: '', end: '' },
            toValue: newTimeline
          }
        });
      }
    }

    // Log team change (handle both teamId and teamIds)
    const oldTeamIds = currentData.teamIds || (currentData.teamId ? [currentData.teamId] : []);
    const newTeamIds = cleanUpdates.teamIds || (cleanUpdates.teamId ? [cleanUpdates.teamId] : []);

    if (JSON.stringify(oldTeamIds.sort()) !== JSON.stringify(newTeamIds.sort())) {
      await logProjectActivity(orgId, {
        projectId,
        type: 'team_changed',
        actorId,
        actorName,
        metadata: {
          fromValue: oldTeamIds,
          toValue: newTeamIds
        }
      });
    }
  }
}

// Update project state with audit logging
export async function updateProjectState(
  orgId: string,
  projectId: string,
  stateId: string,
  actorId: string,
  actorName?: string
): Promise<void> {
  const projectRef = doc(db, 'orgs', orgId, 'projects', projectId);

  // Get current project data
  const currentProject = await getDoc(projectRef);
  if (!currentProject.exists()) {
    throw new Error('Project not found');
  }

  const projectData = currentProject.data();
  const oldStateId = projectData.stateId;
  const oldBucket = projectData.stateCoreBucket;

  // Handle clearing the state (empty string means "No State")
  if (stateId === '') {
    await updateDoc(projectRef, {
      stateId: null,
      stateCoreBucket: null,
      status: 'active',
      updatedAt: serverTimestamp()
    });

    await logProjectActivity(orgId, {
      projectId,
      type: 'state_changed',
      actorId,
      actorName,
      metadata: {
        ...(oldStateId && { fromStateId: oldStateId }),
        toStateId: null,
        ...(oldBucket && { fromBucket: oldBucket }),
        toBucket: null
      }
    });
    return;
  }

  // Handle bucket-level selection (e.g., "bucket-active")
  if (stateId.startsWith('bucket-')) {
    const bucket = stateId.replace('bucket-', '') as 'planned' | 'active' | 'done';

    await updateDoc(projectRef, {
      stateId: null,
      stateCoreBucket: bucket,
      status: BUCKET_TO_STATUS_MAP[bucket] || 'active',
      updatedAt: serverTimestamp()
    });

    await logProjectActivity(orgId, {
      projectId,
      type: 'state_changed',
      actorId,
      actorName,
      metadata: {
        ...(oldStateId && { fromStateId: oldStateId }),
        toStateId: null,
        ...(oldBucket && { fromBucket: oldBucket }),
        toBucket: bucket
      }
    });
    return;
  }

  // Get the new state to determine bucket
  const newState = await getProjectState(orgId, stateId);
  if (!newState) {
    throw new Error(`Project state "${stateId}" does not exist. Please refresh the page and try again.`);
  }
  if (!newState.active) {
    throw new Error(`Project state "${newState.label}" is inactive and cannot be used.`);
  }

  // Get old state info
  let oldStateLabel = oldStateId;
  if (oldStateId) {
    const oldState = await getProjectState(orgId, oldStateId);
    if (oldState) {
      oldStateLabel = oldState.label;
    }
  }

  // Update project with new state (sync legacy status for backward compat)
  await updateDoc(projectRef, {
    stateId,
    stateCoreBucket: newState.coreBucket,
    status: BUCKET_TO_STATUS_MAP[newState.coreBucket] || 'active',
    updatedAt: serverTimestamp()
  });

  // Log audit event
  await logAuditEvent(
    AuditAction.PROJECT_STATE_CHANGED,
    {
      orgId,
      actorId,
      actorEmail: '',
      actorName: actorName || '',
    },
    {
      targetId: projectId,
      targetType: 'project',
      metadata: {
        projectName: projectData.name,
        ...(oldStateId && { fromStateId: oldStateId }),
        toStateId: stateId,
        ...(oldStateLabel && { fromLabel: oldStateLabel }),
        toLabel: newState.label,
        ...(oldBucket && { fromBucket: oldBucket }),
        toBucket: newState.coreBucket
      },
      description: `Moved "${projectData.name}" to ${newState.label}${oldStateLabel ? ` from ${oldStateLabel}` : ''}`
    }
  );

  // Log project activity
  await logProjectActivity(orgId, {
    projectId,
    type: 'state_changed',
    actorId,
    actorName,
    metadata: {
      ...(oldStateId && { fromStateId: oldStateId }),
      toStateId: stateId,
      ...(oldStateLabel && { fromLabel: oldStateLabel }),
      toLabel: newState.label,
      ...(oldBucket && { fromBucket: oldBucket }),
      toBucket: newState.coreBucket
    }
  });
}

export async function deleteProject(
  orgId: string,
  projectId: string,
  deletedBy?: User,
  soft = true
) {
  const projectRef = doc(db, 'orgs', orgId, 'projects', projectId);

  if (soft) {
    // Get project details for audit
    const projectDoc = await getDoc(projectRef);
    const projectData = projectDoc.exists() ? projectDoc.data() : null;

    // Soft delete - mark as deleted
    await updateDoc(projectRef, {
      deletedAt: serverTimestamp(),
      status: 'archived',
      updatedAt: serverTimestamp()
    });

    // Audit log
    if (deletedBy && projectData) {
      await auditProjectDeletion(
        {
          orgId,
          actorId: deletedBy.uid,
          actorEmail: deletedBy.email || undefined,
          actorName: deletedBy.displayName || undefined
        },
        projectId,
        projectData.name
      );
    }
  } else {
    // Hard delete - permanently remove
    await deleteDoc(projectRef);
  }
}

export async function getTeamProjects(
  orgId: string,
  teamId: string,
  includeDeleted = false
): Promise<Project[]> {
  // Get all projects and filter in memory to support both teamId and teamIds
  const projectsQuery = query(
    collection(db, 'orgs', orgId, 'projects'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(projectsQuery);

  const projects = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Project[];

  // Filter for projects associated with this team (either via teamId or teamIds array)
  const teamProjects = projects.filter(p => {
    const projectTeamIds = p.teamIds || (p.teamId ? [p.teamId] : []);
    return projectTeamIds.includes(teamId);
  });

  // Filter out soft-deleted projects unless explicitly requested
  return includeDeleted ? teamProjects : teamProjects.filter(p => !p.deletedAt);
}

export async function addProjectContributor(
  orgId: string,
  projectId: string,
  userId: string,
  actorId?: string,
  actorName?: string,
  contributorName?: string
) {
  const projectRef = doc(db, 'orgs', orgId, 'projects', projectId);
  const projectDoc = await getDoc(projectRef);

  if (projectDoc.exists()) {
    const contributors = projectDoc.data().contributors || [];
    if (!contributors.includes(userId)) {
      await updateDoc(projectRef, {
        contributors: [...contributors, userId],
        updatedAt: serverTimestamp()
      });

      // Log activity
      if (actorId) {
        await logProjectActivity(orgId, {
          projectId,
          type: 'contributor_added',
          actorId,
          actorName,
          metadata: {
            contributorId: userId,
            contributorName
          }
        });
      }
    }
  }
}

export async function removeProjectContributor(
  orgId: string,
  projectId: string,
  userId: string,
  actorId?: string,
  actorName?: string,
  contributorName?: string
) {
  const projectRef = doc(db, 'orgs', orgId, 'projects', projectId);
  const projectDoc = await getDoc(projectRef);

  if (projectDoc.exists()) {
    const contributors = projectDoc.data().contributors || [];
    await updateDoc(projectRef, {
      contributors: contributors.filter((c: string) => c !== userId),
      updatedAt: serverTimestamp()
    });

    // Log activity
    if (actorId) {
      await logProjectActivity(orgId, {
        projectId,
        type: 'contributor_removed',
        actorId,
        actorName,
        metadata: {
          contributorId: userId,
          contributorName
        }
      });
    }
  }
}

export async function transferProjectOwnership(
  orgId: string,
  projectId: string,
  newOwnerId: string,
  newOwnerEmail: string,
  transferredBy: User
) {
  const projectRef = doc(db, 'orgs', orgId, 'projects', projectId);
  const projectDoc = await getDoc(projectRef);

  if (!projectDoc.exists()) {
    throw new Error('Project not found');
  }

  const projectData = projectDoc.data();

  // Update project owner
  await updateDoc(projectRef, {
    ownerUserId: newOwnerId,
    updatedAt: serverTimestamp()
  });

  // Audit log
  await auditOwnershipTransfer(
    {
      orgId,
      actorId: transferredBy.uid,
      actorEmail: transferredBy.email || undefined,
      actorName: transferredBy.displayName || undefined
    },
    'project',
    projectId,
    projectData.name,
    newOwnerId,
    newOwnerEmail
  );
}

export async function getCompletedProjects(
  orgId: string,
  startDate?: Date,
  endDate?: Date
): Promise<Project[]> {
  let projectsQuery = query(
    collection(db, 'orgs', orgId, 'projects'),
    where('status', '==', 'completed')
  );
  
  if (startDate) {
    projectsQuery = query(
      collection(db, 'orgs', orgId, 'projects'),
      where('status', '==', 'completed'),
      where('completedAt', '>=', Timestamp.fromDate(startDate)),
      orderBy('completedAt', 'desc')
    );
    
    if (endDate) {
      projectsQuery = query(
        collection(db, 'orgs', orgId, 'projects'),
        where('status', '==', 'completed'),
        where('completedAt', '>=', Timestamp.fromDate(startDate)),
        where('completedAt', '<=', Timestamp.fromDate(endDate)),
        orderBy('completedAt', 'desc')
      );
    }
  } else {
    projectsQuery = query(
      collection(db, 'orgs', orgId, 'projects'),
      where('status', '==', 'completed'),
      orderBy('completedAt', 'desc')
    );
  }
  
  const snapshot = await getDocs(projectsQuery);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Project[];
}

// Helper function to get projects completed in current quarter
export async function getProjectsCompletedInQuarter(
  orgId: string,
  quarter: number,
  year: number,
  useFiscalYear: boolean = false,
  fiscalYearStartMonth: number = 1
): Promise<Project[]> {
  let startMonth: number;
  let endMonth: number;
  
  if (useFiscalYear) {
    // Calculate fiscal quarter months
    const fiscalMonthOffset = fiscalYearStartMonth - 1;
    startMonth = ((quarter - 1) * 3 + fiscalMonthOffset) % 12;
    endMonth = (startMonth + 2) % 12;
  } else {
    // Calendar quarter
    startMonth = (quarter - 1) * 3;
    endMonth = startMonth + 2;
  }
  
  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, endMonth + 1, 0, 23, 59, 59, 999); // Last day of end month
  
  return getCompletedProjects(orgId, startDate, endDate);
}

// Helper function to get projects completed in fiscal/calendar year
export async function getProjectsCompletedInYear(
  orgId: string,
  year: number,
  useFiscalYear: boolean = false,
  fiscalYearStartMonth: number = 1
): Promise<Project[]> {
  let startDate: Date;
  let endDate: Date;
  
  if (useFiscalYear) {
    startDate = new Date(year, fiscalYearStartMonth - 1, 1);
    endDate = new Date(year + 1, fiscalYearStartMonth - 1, 0, 23, 59, 59, 999);
  } else {
    startDate = new Date(year, 0, 1);
    endDate = new Date(year, 11, 31, 23, 59, 59, 999);
  }
  
  return getCompletedProjects(orgId, startDate, endDate);
}
